import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

const ThemeSelectorModal = ({
  show,
  onHide,
  currentTheme,
  currentPrimaryColor,
  onThemeChange,
  onPrimaryColorChange,
}) => {
  const [customColorInput, setCustomColorInput] = useState(
    currentPrimaryColor || "#000000",
  );

  useEffect(() => {
    setCustomColorInput(currentPrimaryColor || "#000000");
  }, [currentPrimaryColor]);

  const themes = [
    {
      key: "modern",
      name: "Modern",
      category: "Professional",
      screenshot: "/assets/images/theme-previews/modern-theme.png",
    },
    {
      key: "vintage",
      name: "Vintage",
      category: "Traditional",
      screenshot: "/assets/images/theme-previews/vintage-theme.png",
    },
    {
      key: "premium",
      name: "Premium",
      category: "Liquid Glass",
      screenshot: "/assets/images/theme-previews/premium-theme.png",
    },
  ];

  const handleThemeSelect = (themeKey) => {
    onThemeChange(themeKey);
    onHide();
  };

  const handlePrimaryColorChange = (event) => {
    const colorValue = event.target.value;
    setCustomColorInput(colorValue);
    onPrimaryColorChange(colorValue);
  };

  const normalizeHexColor = (value) => {
    const rawValue = value.trim();
    const withHash = rawValue.startsWith("#") ? rawValue : `#${rawValue}`;
    const isValidHex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(withHash);

    if (!isValidHex) {
      return null;
    }

    if (withHash.length === 4) {
      const r = withHash[1];
      const g = withHash[2];
      const b = withHash[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }

    return withHash;
  };

  const applyCustomColorInput = () => {
    const normalizedColor = normalizeHexColor(customColorInput || "");

    if (!normalizedColor) {
      setCustomColorInput(currentPrimaryColor || "#000000");
      return;
    }

    setCustomColorInput(normalizedColor);
    onPrimaryColorChange(normalizedColor);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="md"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center gap-2">
            <span>Choose Your Theme</span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: "70vh", overflowY: "auto", padding: "1rem" }}
      >
        <div className="mb-3">
          <label
            htmlFor="primary-theme-color"
            className="form-label mb-2"
            style={{
              color: "var(--theme-text-primary, #1f2937)",
              fontWeight: 600,
            }}
          >
            Primary Color
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="primary-theme-color"
              type="color"
              value={currentPrimaryColor || "#000000"}
              onChange={handlePrimaryColorChange}
              aria-label="Choose primary color"
              style={{
                width: "44px",
                height: "38px",
                border: "1px solid var(--theme-border, #e2e8f0)",
                borderRadius: "8px",
                background: "transparent",
                cursor: "pointer",
              }}
            />
            <input
              type="text"
              value={customColorInput}
              onChange={(event) => setCustomColorInput(event.target.value)}
              onBlur={applyCustomColorInput}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  applyCustomColorInput();
                }
              }}
              placeholder="#000000"
              aria-label="Enter primary color hex code"
              style={{
                width: "110px",
                height: "38px",
                border: "1px solid var(--theme-border, #e2e8f0)",
                borderRadius: "8px",
                padding: "0 10px",
                color: "var(--theme-text-secondary, #6b7280)",
              }}
            />
          </div>
        </div>

        <div className="row g-2">
          {themes.map((theme) => (
            <div key={theme.key} className="col-12 col-md-4">
              <div
                className={`card theme-card ${
                  currentTheme === theme.key ? "border-primary" : ""
                }`}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border:
                    currentTheme === theme.key
                      ? "2px solid var(--theme-primary)"
                      : "1px solid var(--theme-border, #e2e8f0)",
                  position: "relative",
                }}
                onClick={() => handleThemeSelect(theme.key)}
              >
                {currentTheme === theme.key && (
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      backgroundColor: "var(--theme-primary)",
                      color: "white",
                      borderRadius: "50%",
                      width: "26px",
                      height: "26px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1,
                    }}
                  >
                    <iconify-icon
                      icon="mdi:check-bold"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                )}

                {/* Theme Screenshot */}
                <div
                  className={
                    theme.key === "premium" ? "theme-preview-premium" : ""
                  }
                  style={{
                    width: "100%",
                    height: "130px",
                    overflow: "hidden",
                    backgroundColor: "var(--theme-surface, #f8fafc)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {theme.key === "premium" ? (
                    <div className="premium-theme-card-preview" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                  ) : (
                    <img
                      src={theme.screenshot}
                      alt={theme.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.innerHTML = `
                        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: var(--theme-text-secondary, #6b7280);">
                          <iconify-icon icon="solar:palette-line-duotone" style="font-size: 40px; margin-bottom: 6px;"></iconify-icon>
                          <span style="font-size: 13px;">${theme.name}</span>
                        </div>
                      `;
                      }}
                    />
                  )}
                </div>

                {/* Theme Details */}
                <div className="card-body" style={{ padding: "0.75rem" }}>
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h6
                      className="card-title mb-0"
                      style={{ fontWeight: "600", fontSize: "14px" }}
                    >
                      {theme.name}
                    </h6>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "var(--theme-primary-light, #dbeafe)",
                        color: "var(--theme-primary)",
                        fontSize: "10px",
                        padding: "3px 6px",
                      }}
                    >
                      {theme.category}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer></Modal.Footer>
    </Modal>
  );
};

export default ThemeSelectorModal;
